import {Inject, Injectable} from "@angular/core";
import {DOCUMENT} from "@angular/common";
import {concatMap, exhaustMap, Observable, of, race, switchMap, take, tap, throwError, timer} from "rxjs";
import {BankIdSigneringStatus, BankidSigneringStatusModell, DokumentId, DokumentSignatarModell, DokumentsigneringRessursClient, DokumentVersjonModell, SignatarId, Signeringsmetode} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {delay, distinctUntilChanged, filter, map, takeWhile} from "rxjs/operators";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {i18n, I18nTekst, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import SignUrlModell = l.SignUrlModell;
import {åpneINyttvindu} from "felles-ng2/støttefunksjoner/nettleser";

const debug = initDebugger('SigneringBankIDQESTjeneste');

const bankidTerminerendeStatuser = new Set([
    BankIdSigneringStatus.signering_fullfort,
    BankIdSigneringStatus.avvist,
    BankIdSigneringStatus.feilet,
    BankIdSigneringStatus.utlopt
]);

/**
 * Sørger for å starte en "signeringsordre" mot BankID
 * Ved å kalle signerUrl sender Styreplan-api kall til BankID for å opprette signeringsordre (dokument til signering lastes opp).
 * Styreplan-api returnerer en ferdig URL med iden til signeringsordren som vi bruker i popup for å sende brukeren til BankID for signering av dokumentet
 * Tilstand om signering ivaretas på klienten i denne tjenesten gjennom observable som returneres.
 * Observable poller regelmessig BankID gjennom Styreplan-api for å få status på signering.
 * Når signeringen er fullført gjør vi kall mot Styreplan-api for å initere å hente dokumentet
 * Observable returnerer løpende frem til den er ferdig signert og Styreplan-apiet returnerer at
 */
@Injectable()
export class SigneringBankIDQESTjeneste {

    constructor(
      @Inject(DOCUMENT) private document: Document,
      private readonly dokSignRessurs: DokumentsigneringRessursClient,
      private readonly oversettelser:Oversettelser) {}



    startBankIDSigneringPopup(dokumentVersjon: DokumentVersjonModell, signatar: DokumentSignatarModell | null): Observable<BankIdSigneringStatus> {
        const popoup = this.opprettPopup(dokumentVersjon.pdfFilnavn!); // Last før vi har fått tak i URLen for å hindre at nettleser (Safari spesielt) trenger at selve popupen skjer synkront i sammenheng med klikket på signer-knappen.

        if(!popoup) {
            debug("popup blokkert")
            return throwError(() => new BankidKlientFeilError(BankidKlientFeil.PopupBlokkert, "popup blokkert av nettleser", new I18nTekst(i18n("Styreplan kunne ikke åpne et nytt vindu for å fortsette signeringsprosessen. Sjekk om nettleseren har blokkert nytt vindu/popups i adressefeltet.")).oversett(this.oversettelser)));
        }

        return this.dokSignRessurs.signerUrl(dokumentVersjon.dokumentId, Signeringsmetode.norsk_bankid_netsentrisk_pdf).somObservable()
          .pipe(
            take(1),
            delay(2000), // ønske fra Henry for at brukeren skulle kunne forstå litt mer om hva som skjer når nytt vindu vises
            switchMap((signering:SignUrlModell) => {
                if(popoup != null) {
                    // Må sette denne i etterkant, nettleser (Safari spesielt) trenger at selve popupen skjer synkront i sammenheng med klikket på signer-knappen.
                    popoup.window.location.href = signering.bankidSigneringOrdre.signUrl;
                }

                // TODO hvor lenge skal vi vente mellom statuskall? Om BankID sitt gui går tregt, men signering er fullført risikerer vi å slette signerte dokument
                //  (Fra server pga. bankidpdfsignert gjør DELETE) før brukeren får se det i grensesnittet og kan se kvittering.
                // 16.03.26 BankID opplyser at dette angivelig skal rettes "snart"..
                return this.sjekkStatus(signering.bankidSigneringOrdre.signId, dokumentVersjon.dokumentId, signatar!.id, 5); // signatar skal alltid finnes når vi kommer hit, siden det er en forutsetning for at vi skal starte signering fra signer dialog
            }));
    }

    /**
     * Oppretter et popup vindu med signeringsurl for BankID brukersignering.
     * @param signeringUrl URL til BankID signering for bruker.
     */
    private opprettPopup(filnavn:string) {
        const screenWidth = this.document.defaultView!.screen.width;
        const screenHeight = this.document.defaultView!.screen.height;

        const height = Math.min(1600, Math.floor(screenHeight * 0.8));
        const width = Math.min(1200, Math.floor(screenWidth * 0.8));
        const left = (screenWidth - width) / 2;
        const top = (screenHeight - height) / 2;

        // Siden vi åpner nytt vindu med _blank ønsker vi å vise brukeren kort noe om hva som skjer
        const htmlPlaceholder = `
        <html>
        <head>
            <style>
                body { font-family: sans-serif; padding: 20px; font-size: 0.8em;  }
                .boks { padding: 20px; border-radius: 8px; background-color: #D1E6FF; width: 60%;}
            </style>
        </head>
        <body style="display: flex; justify-content: center; align-items: center; height: 100vh;">
        <div class="boks">
            <h1>${new I18nTekst(i18n('Signering med Styreplan')).oversett(this.oversettelser)}</h1>
            <h2>${new I18nTekst(i18n("Vent litt. Vi sender deg straks videre til signering av dokumentet '{filnavn}' hos BankID."),{filnavn: filnavn}).oversett(this.oversettelser)}</h2>
        </div>
        </body>
        </html>
        `

        const htmlBlob = new Blob([htmlPlaceholder], { type: 'text/html' });
        const htmlBlobUrl = URL.createObjectURL(htmlBlob)

        return åpneINyttvindu(htmlBlobUrl,'_blank');
    }

    private sjekkStatus(signid: string, dokumentId: DokumentId, signatarId: SignatarId, intervallSekunder = 5): Observable<BankIdSigneringStatus> {

        // Poller for status frem til terminerende status, da avsluttes strømmen og abonnenter må håndtere dette hos seg.
        const polling$ = timer(1000, intervallSekunder * 1000).pipe(
          exhaustMap(() =>
            this.dokSignRessurs.bankidPdfSigneringStatus({dokumentId: dokumentId, signatarId: signatarId, sign_id: signid}).somObservable().pipe(
              concatMap((statusModell: BankidSigneringStatusModell) => {

                  if (statusModell.status !== BankIdSigneringStatus.signering_fullfort) {
                      return of(statusModell);
                  }

                  return this.dokSignRessurs.bankidPdfSignert({dokumentId, signatarId, sign_id: signid})
                    .somObservable()
                    .pipe(map(() => statusModell));
              }),
              map(statusModell => statusModell.status as BankIdSigneringStatus),
              tap(status => debug('signeringStatus:', status)),
            )
          ),
          filter(erIkkeNil),
          distinctUntilChanged(),
          takeWhile(status => !bankidTerminerendeStatuser.has(status), true),
        );

        // Hvis vi ønsker å begrense hvor lenge brukeren ser statusen "ordre_motatt" på klienten,
        // men merk at denne ikke kan fortsette å løpe når brukeren går over i statusen "user_signing".
        // Vi kan heller ikke lukke bankid-popupvinduet når det er på en annen origin, så brukeren kan i prinsippet fortsette å signere, mens vi har stoppet prosessen i Styreplan
        // const tidsbegrensning$ = timer(60 * 1000).pipe(
        //   map((): BankIdSigneringStatus => BankIdSigneringStatus.utlopt),
        //   tap(() => console.log("timet ut"))
        // );
        //         return race(polling$, tidsbegrensning$).pipe(
        //           tap(status => console.log('signeringStatus:', status)),
        //         );

        // Whichever of the three streams emits first "wins" — the others are unsubscribed
        return polling$;
    }
}

export const BANKID_TERMINERENDE_STATUSER = new Set<BankIdSigneringStatus>([
    BankIdSigneringStatus.signering_fullfort,
    BankIdSigneringStatus.avvist,
    BankIdSigneringStatus.feilet,
    BankIdSigneringStatus.utlopt,
]);

export enum BankidKlientFeil {
    PopupBlokkert = "bankid_popup_blokkert"
}

export class BankidKlientFeilError extends Error {
    constructor(readonly klientFeil: BankidKlientFeil, readonly feilmelding: string, readonly brukerrettetMelding:string) {
        super(feilmelding);
    }
}
